import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { GruppeTier } from '../../../gruppe-tier/classes/gruppe-tier';
import { KieneBackendApiService } from '../../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { GruppeAbgangService } from '../gruppe-abgang.service';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { EnterValueDialogComponent } from '../../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../../dialogs/enter-value-dialog/enter-value-response';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { AppService } from '../../../../services/app/app.service';
import { SoundType } from '../../../../classes/sound-type';
import { GruppenService } from '../../gruppen.service';
import { Tierliste } from '../../../gruppe-tier/classes/tierliste';
import { appendAbgangTierLookupGruppeFilter } from '../abgang-tier-lookup.util';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import {
    isValidOhrmarkePassScanLength,
    normalizeOhrmarkePassScanInput,
    OHRMARKE_SCAN_LENGTH,
    OHRMARKE_WITH_CHECK_DIGITS_LENGTH,
    OhrmarkeScanTrailGuard,
    PASS_SCAN_LENGTH,
} from '../../../../utils/ohrmarke-pass-scan.util';

export class AbgangScanResult {
    ohrmarke: string;
    success: boolean;
}

@Component({
    selector: 'kiene-abgang-scannen',
    templateUrl: './abgang-scannen.component.html',
    styleUrls: ['./abgang-scannen.component.scss'],
    animations: [
        trigger('openClose', [
            state('open', style({ backgroundColor: 'rgba(55, 126, 34, 0.8)' })),
            state('closed', style({ backgroundColor: 'rgba(55, 126, 34, 0)' })),
            transition('open => closed', [animate('1s')]),
            transition('closed => open', [animate('0.5s')]),

            state('openDuplicate', style({ backgroundColor: 'rgba(255, 150, 45, 0.8)' })),
            state('closeDuplicate', style({ backgroundColor: 'rgba(255, 150, 45, 0)' })),
            transition('openDuplicate => closeDuplicate', [animate('1s')]),
            transition('closeDuplicate => openDuplicate', [animate('0.5s')]),

            state('openError', style({ backgroundColor: 'rgba(155, 0, 36, 0.8)' })),
            state('closedError', style({ backgroundColor: 'rgba(155, 0, 36, 0)' })),
            transition('openError => closedError', [animate('1s')]),
            transition('closedError => openError', [animate('0.5s')]),
        ]),
    ],
})
export class AbgangScannenComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('ohrenmarkeInput') ohrenmarkeInput: MatInput;

    @Input('abgangsliste') abgangsliste: Tierliste;

    ohrmarkePassCtrl = new UntypedFormControl(0);

    ohrenmarkeCtrl = new UntypedFormControl(undefined);
    private readonly trailGuard = new OhrmarkeScanTrailGuard();
    private ohrenmarkeTrailSub?: Subscription;

    gescannteOhrmarken: AbgangScanResult[] = [];
    openCloseState = 'closed';

    ladeOhrmarkeSub: Subscription;

    maxLengthOhrmarke = OHRMARKE_WITH_CHECK_DIGITS_LENGTH;

    mitGewichtToggleCtrl = new UntypedFormControl(false);
    selectedTier: GruppeTier;

    gruppeTierSubscription: Subscription;

    constructor(
        private api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private abgangService: GruppeAbgangService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private appService: AppService,
        private gruppenService: GruppenService,
        private localStorageService: LocalStorageService,
    ) {}

    ngOnDestroy(): void {
        this.trailGuard.destroy();
        this.ohrenmarkeTrailSub?.unsubscribe();
        this.ladeOhrmarkeSub?.unsubscribe();
    }

    ngOnInit(): void {
        this.loadScanHistory();
        this.ohrenmarkeTrailSub = this.ohrenmarkeCtrl.valueChanges.subscribe(value => {
            if (this.trailGuard.shouldIgnoreIncoming() && value) {
                this.ohrenmarkeCtrl.setValue('', { emitEvent: false });
            }
        });
        this.abgangService.watchAbgangsliste().subscribe({
            next: reload => {
                if (reload?.tierliste) {
                    this.abgangsliste = reload.tierliste;
                }
                this.loadScanHistory();
            },
        });
        this.applyScanModeValidators();
        this.ohrmarkePassCtrl.valueChanges.subscribe(value => {
            if (value === 1) {
                this.maxLengthOhrmarke = PASS_SCAN_LENGTH;
            } else {
                this.maxLengthOhrmarke = OHRMARKE_WITH_CHECK_DIGITS_LENGTH;
            }
            this.applyScanModeValidators();
        });
    }

    get canSubmitScan(): boolean {
        return isValidOhrmarkePassScanLength(String(this.ohrenmarkeCtrl.value ?? ''));
    }

    private applyScanModeValidators(): void {
        if (this.ohrmarkePassCtrl.value === 1) {
            this.ohrenmarkeCtrl.setValidators([Validators.minLength(PASS_SCAN_LENGTH), Validators.maxLength(PASS_SCAN_LENGTH)]);
        } else {
            this.ohrenmarkeCtrl.setValidators([
                Validators.minLength(OHRMARKE_SCAN_LENGTH),
                Validators.maxLength(OHRMARKE_WITH_CHECK_DIGITS_LENGTH),
            ]);
        }
        this.ohrenmarkeCtrl.updateValueAndValidity({ emitEvent: false });
    }

    private getNormalizedScanValue(): string {
        return normalizeOhrmarkePassScanInput(String(this.ohrenmarkeCtrl.value ?? ''));
    }

    private syncNormalizedScanValue(): string {
        const normalized = this.getNormalizedScanValue();
        if (normalized !== this.ohrenmarkeCtrl.value) {
            this.ohrenmarkeCtrl.setValue(normalized, { emitEvent: false });
        }
        return normalized;
    }

    clear() {
        this.ohrmarkePassCtrl.setValue(undefined, { emitEvent: false });
        this.selectedTier = undefined;
        this.ohrenmarkeInput?.focus();
    }

    ngAfterViewInit(): void {
        this.ohrenmarkeInput?.focus();
    }

    isInputValid(): boolean {
        const normalized = this.syncNormalizedScanValue();
        if (this.ohrmarkePassCtrl.value === 0) {
            if (normalized.length === OHRMARKE_SCAN_LENGTH) {
                return true;
            }
            this.messageService.alertMessage('Die Länge der Ohrmarke ist ungültig!');
        }
        if (this.ohrmarkePassCtrl.value === 1) {
            if (normalized.length === PASS_SCAN_LENGTH) {
                return true;
            }
            this.messageService.alertMessage('Die Länge der Passnummer ist ungültig!');
        }
        this.ohrenmarkeCtrl.setValue('');
        this.trailGuard.armTrailingIgnore();
        return false;
    }

    checkPassOhrmarke(gewichtInKg?: number, abteil?: string) {
        if (!this.isInputValid()) {
            return;
        }
        let params = appendAbgangTierLookupGruppeFilter(new HttpParams(), this.abgangsliste, {
            kundeIdFallback: this.localStorageService.getCurrentClient()?.kunde_id,
        });

        if (this.ohrmarkePassCtrl.value === 0) {
            // ohrmarke gescannt
            params = params.set('ohrmarke_kurz', this.ohrenmarkeCtrl.value);
        } else if (this.ohrmarkePassCtrl.value === 1) {
            // pass gescannt
            params = params.set('ohrmarke15', this.ohrenmarkeCtrl.value);
        }

        params = params.set('tot', '0');
        if (this.abgangsliste?.tierliste_id) {
            params = params.set('tierliste_id_or_null', String(this.abgangsliste.tierliste_id));
        }

        this.ladeOhrmarkeSub?.unsubscribe();
        this.ladeOhrmarkeSub = this.api
            .getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params)
            .subscribe({
                next: response => {
                    if (response.count === 1) {
                        this.selectedTier = response.records[0];
                        this.melden(gewichtInKg, abteil);
                    } else if (response.count > 1) {
                        this.messageService.alertMessage('Mehr als ein Tier gefunden!');
                        this.ohrenmarkeCtrl.setValue('');
                        this.trailGuard.armTrailingIgnore();
                        this.ohrenmarkeInput?.focus();
                    } else {
                        this.messageService.alertMessage('Kein Tier mit diesen Daten gefunden!');
                        this.ohrenmarkeCtrl.setValue('');
                        this.trailGuard.armTrailingIgnore();
                        this.ohrenmarkeInput?.focus();
                    }
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
    }

    melden(weight?: number, abteil?: string) {
        this.appService.setBlockOnLoading(true);
        const obj = {
            tierliste_id: this.abgangsliste?.tierliste_id,
            gruppen_tiere: [{ gt_id: this.selectedTier.gt_id, abgangsgewicht: weight, abteil_abgang: abteil }],
        };
        this.api.post(this.apiBaseUrl + 'tierliste/abgang_kundenportal.php', obj, undefined).subscribe({
            next: (tierlisteAntwort: Tierliste) => {
                this.abgangService.setAbgangsliste(tierlisteAntwort, true, true);
                this.appService.setBlockOnLoading(false);
                this.ohrenmarkeCtrl.enable();

                this.recordScanHistory(this.getScanHistoryOhrmarke(), true);
                this.gruppenService.playScanSound(SoundType.OK);
                this.openCloseState = 'closed';
                this.openCloseState = 'open';
                this.ohrenmarkeCtrl.setValue(undefined, { emitEvent: false });
                this.trailGuard.armTrailingIgnore();
                this.ohrenmarkeInput?.focus();
                this.selectedTier = undefined;
                setTimeout(() => {
                    this.openCloseState = 'closed';
                }, 400);
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
                this.appService.setBlockOnLoading(false);
                this.ohrenmarkeCtrl.enable();
                this.gruppenService.playScanSound(SoundType.ERROR);
                this.recordScanHistory(this.getScanHistoryOhrmarke(), false);
                this.openCloseState = 'openError';
                setTimeout(() => {
                    this.openCloseState = 'closedError';
                    this.ohrenmarkeCtrl.setValue('');
                    this.trailGuard.armTrailingIgnore();
                    this.ohrenmarkeInput?.focus();
                }, 400);
            },
        });
    }

    openWeightDailog() {
        const input = new EnterValueInput();
        input.title = 'Gewicht oder Abteil eingeben';
        input.text = 'Es kann optional ein Gewicht oder Abteil angegeben werden';
        input.okButton = 'Speichern';
        input.buttonColor = 'accent';
        input.addInputValue('Gewicht in kg (otional)', 'decimal');
        input.addInputValue('Abteil (optional)', 'text', this.abgangService.abteilAbgang);

        const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input, position: { top: '10px' } });
        console.log(dialogRef);
        dialogRef.afterClosed().subscribe({
            next: (response: EnterValueResponse) => {
                if (response) {
                    if (response.result) {
                        const gewicht = response.getValue(0);
                        const abteil = response.getValue(1);
                        this.abgangService.abteilAbgang = abteil;
                        this.checkPassOhrmarke(gewicht, abteil);
                    } else {
                        this.checkPassOhrmarke();
                    }
                }
            },
        });
    }

    addOhrmarkePass($event) {
        $event.stopPropagation();
        this.trailGuard.settleThenRun(
            () => String(this.ohrenmarkeCtrl.value ?? ''),
            () => {
                if (!this.isInputValid()) {
                    return;
                }
                // Collapse check digits in the field before lookup; arm ignore for any late keystrokes.
                this.syncNormalizedScanValue();
                this.trailGuard.armTrailingIgnore();
                this.mitGewichtToggleCtrl.value ? this.openWeightDailog() : this.checkPassOhrmarke();
            },
        );
    }

    private loadScanHistory(): void {
        const tierlisteId = Number(this.abgangsliste?.tierliste_id || 0);
        this.gescannteOhrmarken = this.abgangService.getScanHistory(tierlisteId).map(entry => {
            const scanResult = new AbgangScanResult();
            scanResult.ohrmarke = entry.ohrmarke;
            scanResult.success = entry.success;
            return scanResult;
        });
    }

    private recordScanHistory(ohrmarke: string, success: boolean): void {
        const tierlisteId = Number(this.abgangsliste?.tierliste_id || 0);
        this.abgangService.pushScanHistory(tierlisteId, ohrmarke, success);
        this.loadScanHistory();
    }

    private getScanHistoryOhrmarke(): string {
        if (this.ohrmarkePassCtrl.value === 0) {
            return this.selectedTier?.ohrmarke_kurz || this.selectedTier?.ohrmarke_notnull || '';
        }
        return this.selectedTier?.ohrmarke15 || this.selectedTier?.ohrmarke_notnull || '';
    }
}
